/**
 * ページトップへのリンク機能を管理
 *
 * 仕様
 * - ist-footer__pageTop: リンクの親要素
 * - ist-footer__pageTopLink: ページトップへのリンク
 *
 * 使い方
 * <div class="ist-footer__pageTop">
 *   <a href="#" class="ist-footer__pageTopLink">
 *     <!-- SVGやテキスト -->
 *   </a>
 * </div>
 */

/**
 * ページトップリンクを管理するクラス
 */
export class PageTopLink {
  private container: Element;
  private link: HTMLElement | null;
  private boundHandleClick: (event: MouseEvent) => void;
  private boundHandleScroll: () => void;

  constructor(container: Element) {
    this.container = container;
    this.link = container.querySelector('.ist-footer__pageTopLink');

    // バインドしたメソッドを保持してremoveEventListener時に使用
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleScroll = this.handleScroll.bind(this);

    this.init();
  }

  /**
   * 初期化処理
   */
  private init(): void {
    if (!this.link) return;

    // リンクのクリックイベントを追加
    this.link.addEventListener('click', this.boundHandleClick);
  }

  /**
   * リンククリック時のハンドラー
   * @param event マウスイベント
   */
  private handleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // スクロールをトップに移動
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    // スクロール完了を監視
    window.addEventListener('scroll', this.boundHandleScroll, {
      passive: true,
    });
  }

  /**
   * スクロールイベントのハンドラー
   * トップにスクロールされたら最初のフォーカス可能な要素にフォーカスを設定
   */
  private handleScroll(): void {
    if (window.scrollY === 0) {
      // 最初のフォーカス可能な要素を取得
      const firstFocusable = this.getFirstFocusableElement();

      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        // フォーカス可能な要素がない場合はbodyにフォーカス
        (document.body as HTMLElement).focus();
      }

      // スクロールイベントのリスナーを削除
      window.removeEventListener('scroll', this.boundHandleScroll);
    }
  }

  /**
   * ページ内の最初のフォーカス可能な要素を取得するヘルパー関数
   * @returns 最初のフォーカス可能なHTMLElementまたはnull
   */
  private getFirstFocusableElement(): HTMLElement | null {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input[type="text"]:not([disabled])',
      'input[type="radio"]:not([disabled])',
      'input[type="checkbox"]:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const focusableElements = Array.from(
      document.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
    ).filter((el) => this.isFocusable(el)); // 修正箇所: this.isFocusable に変更

    return focusableElements.length > 0 ? focusableElements[0] : null;
  }

  /**
   * 要素がフォーカス可能かどうかを判定する関数
   * @param element フォーカス可能性を判定するHTMLElement
   * @returns フォーカス可能であればtrue、そうでなければfalse
   */
  private isFocusable(element: HTMLElement): boolean {
    return !element.hasAttribute('disabled') && this.isVisible(element); // 修正箇所: this.isVisible に変更
  }

  /**
   * 要素が表示されているかどうかを判定する関数
   * @param element 表示状態を判定するHTMLElement
   * @returns 表示されていればtrue、そうでなければfalse
   */
  private isVisible(element: HTMLElement): boolean {
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  }

  /**
   * イベントリスナーを解除してクリーンアップ
   */
  public destroy(): void {
    if (this.link) {
      this.link.removeEventListener('click', this.boundHandleClick);
    }
    window.removeEventListener('scroll', this.boundHandleScroll);
  }
}

/**
 * 複数のページトップリンクを管理するマネージャークラス
 */
export class PageTopLinkManager {
  private pageTopLinks: PageTopLink[] = [];

  constructor() {
    this.init();
  }

  /**
   * 初期化処理
   */
  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializePageTopLinks();
      });
    } else {
      this.initializePageTopLinks();
    }
  }

  /**
   * ページトップリンクを初期化
   */
  private initializePageTopLinks(): void {
    const containers = document.querySelectorAll('.ist-footer__pageTop');
    containers.forEach((container) => {
      this.pageTopLinks.push(new PageTopLink(container));
    });
  }

  /**
   * 管理しているすべてのページトップリンクを破棄
   */
  public destroy(): void {
    this.pageTopLinks.forEach((pageTopLink) => pageTopLink.destroy());
    this.pageTopLinks = [];
  }
}
